import { ScanSearch } from 'lucide-react';
import { useTranslation } from '../../../i18n/useTranslation';
import { Alert } from '../../atoms/Alert/Alert';
import { Button } from '../../atoms/Button/Button';
import { EmptyState } from '../../atoms/EmptyState';
import { LiveRegion } from '../../atoms/LiveRegion/LiveRegion';
import { Text } from '../../atoms/Typography/Text';
import { LoadErrorState } from '../../feedback/LoadErrorState/LoadErrorState';
import { Section } from '../../templates/Section/Section';
import { useOverviewAnalysis } from '../../../hooks/useLivestockOverview';
import type { OverviewCreateResult } from '../../../services/livestockOverviewService';
import { OverviewProgress } from './OverviewProgress';
import { OverviewReview } from './OverviewReview';
import { overviewFailureMessage } from './overviewText';

export interface OverviewAnalysisViewProps {
  aquariumId: string;
  analysisId: string;
  /** Back to the upload step (new photo). */
  onNewPhoto: () => void;
  onCreated: (result: OverviewCreateResult) => void;
}

/**
 * One analysis in every state: loading, load error, running (two phases, polled),
 * failed, nothing found (empty state) and the review of the found animals.
 */
export function OverviewAnalysisView({ aquariumId, analysisId, onNewPhoto, onCreated }: OverviewAnalysisViewProps) {
  const { t } = useTranslation();
  const { data: analysis, isLoading, isError, refetch } = useOverviewAnalysis(aquariumId, analysisId);

  if (isLoading) {
    return (
      <LiveRegion busy data-testid="overview-loading">
        <Text color="muted">{t('livestockOverview.load.loading')}</Text>
      </LiveRegion>
    );
  }

  if (isError || !analysis) {
    return (
      <LoadErrorState
        data-testid="overview-load-error"
        title={t('livestockOverview.load.errorTitle')}
        titleLevel={2}
        message={t('livestockOverview.load.errorBody')}
        onRetry={() => void refetch()}
        retryLabel={t('livestockOverview.load.retry')}
      />
    );
  }

  if (analysis.status === 'detecting' || analysis.status === 'identifying') {
    return (
      <Section title={t('livestockOverview.progress.title')}>
        <OverviewProgress status={analysis.status} />
      </Section>
    );
  }

  if (analysis.status === 'failed') {
    return (
      <Alert
        data-testid="overview-failed"
        title={t('livestockOverview.failed.title')}
        titleLevel={2}
        live="polite"
        action={
          <Button variant="primary" onClick={onNewPhoto}>
            {t('livestockOverview.failed.retry')}
          </Button>
        }
      >
        {overviewFailureMessage(t, analysis.errorCode)}
      </Alert>
    );
  }

  if (analysis.candidates.length === 0) {
    return (
      <EmptyState
        icon={<ScanSearch size={48} strokeWidth={1.5} />}
        headline={t('livestockOverview.empty.title')}
        description={t('livestockOverview.empty.body')}
        actionLabel={t('livestockOverview.empty.action')}
        actionVariant="primary"
        headingLevel={2}
        onAction={onNewPhoto}
      />
    );
  }

  return (
    <OverviewReview
      key={analysis.id}
      aquariumId={aquariumId}
      analysis={analysis}
      onCreated={onCreated}
      onDiscarded={onNewPhoto}
    />
  );
}

export default OverviewAnalysisView;
